import React, { useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import { AppNavItems } from "./AppNavItems";
import { Login } from "../login/Login";
import useLogin from "../../common/redux/common/login/useLogin";
import { ApiStatus } from "../../common/services/api-status.enum";
import AppErrorBoundary from "../error/AppErrorBoundary";

// Protected Route wrapper that preserves query params
const ProtectedRoute = ({ loginStatus, children }) => {
  const location = useLocation();
  // Track if user was ever authenticated to prevent unmounting on subsequent loading states
  const wasAuthenticated = useRef(false);
  const isMsalAuthenticated = useIsAuthenticated();

  // Defense-in-depth: require BOTH a profileDetails cache entry AND an active MSAL
  // account. sessionStorage alone is trivially spoofable via DevTools; MSAL ties the
  // signal to a real signed token. Backend remains the ultimate auth boundary.
  const hasStoredAuth =
    sessionStorage.getItem("profileDetails") !== null && isMsalAuthenticated;
  
  if (loginStatus === ApiStatus.SUCCESS) {
    wasAuthenticated.current = true;
    return children;
  }
  
  // If user was previously authenticated, keep showing children during loading
  // This prevents remounting and duplicate API calls
  if (wasAuthenticated.current && loginStatus === ApiStatus.LOADING) {
    return children;
  }
  
  // If we have stored auth (page refresh scenario), show children while waiting for auth check
  if (hasStoredAuth && (loginStatus === ApiStatus.LOADING || loginStatus === ApiStatus.NONE)) {
    return children;
  }
  
  // If still loading on initial load (no stored auth), don't redirect yet - wait for the result
  if (loginStatus === ApiStatus.LOADING) {
    return null; // or a loading spinner
  }
  
  // If NONE (no stored auth) or ERROR, redirect to login
  const postLoginRedirectPath = `${location.pathname}${location.search}`;
  if (postLoginRedirectPath && postLoginRedirectPath !== "/") {
    sessionStorage.setItem("postLoginRedirect", postLoginRedirectPath);
  }
  const redirectPath = `/${location.search}`;
  return <Navigate to={redirectPath} state={{ from: location }} replace />;
};

// Routes that should not be protected (accessible without authentication)
const unprotectedPaths = ['/logout', '/session-expired', '/error', '/maintenance'];

export const MyRoutes = () => {
  const { loginDetail } = useLogin();
  
  const loginStatus = loginDetail?.status;
  
  return (
    <Routes>
      <Route path={"/"} element={<Login />} />
      {AppNavItems.map((item) => {
        // Check if this route should be unprotected
        const isUnprotected = unprotectedPaths.some(path => item.path === path || item.path.startsWith(path));
        const wrappedElement = <AppErrorBoundary>{item.component}</AppErrorBoundary>;

        return (
          <Route 
            key={item.id} 
            path={item.path} 
            element={
              isUnprotected ? (
                wrappedElement
              ) : (
                <ProtectedRoute loginStatus={loginStatus}>
                  {wrappedElement}
                </ProtectedRoute>
              )
            } 
          />
        );
      })}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};